import { applySiteStudyArea } from '../siteStudyArea';
/**
 * Resolve Coverage Matrix setup / intent rows from projectIntent,
 * typedPreferencesDefaults, and intentToPreferences fallbacks.
 */
import type { ChecklistItem } from '../../config/documentChecklists';
import type {
  ProjectIntent,
  TypedPreferencesByType,
  CustomBriefPreferences,
  ExecutiveSummaryPreferences,
  InterconnectionPreferences,
  FeasibilityPreferences,
} from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteContext } from '../../types/siteContext';
import type { SiteEntity } from '../../types/site';
import type { ReportTypeId } from '../../config/reportTypes';
import type { AuthoredFieldDef, AuthoredFieldDeriveContext } from '../../types/authoredEvidence';
import { isFieldFilled } from '../../types/authoredEvidence';
import { formatFieldValue } from '../evidenceLineage/requirementToFields';
import { resolveField, type InputSources } from '../inputResolution';
import { preferencesFromIntent } from '../intentToPreferences';
import { effectivePower } from '../projectIntent';
import { formatWaterSources, waterSourceKinds } from '../intent/waterSource';
import { normaliseCooling, COOLING_TYPE_LABEL } from '../../types/cooling';
import { projectCoolingGeneration } from '../../types/siteRag';
import {
  HEAT_REJECTION_LABELS,
  COOLING_GENERATION_LABELS,
  INTERCONNECTION_STATUS_LABELS,
  INTERCONNECTION_STUDY_PHASE_LABELS,
  FUEL_ACCESS_LABELS,
  WATER_SOURCE_LABELS,
  WATER_RIGHTS_STATUS_LABELS,
  OFFTAKE_STRUCTURE_LABELS,
  CAPACITY_CREDIT_TARGET_LABELS,
} from '../reportPreferences/labels';
import {
  SITE_PROJECT_INTENT_ID,
  getReportSetupChecklistFor,
  reportSetupRequirementId,
  setupReportTypeForRequirement,
} from '../../config/reportSetupRequirements';
import { validatePreferencesFor } from '../reportPreferences/validation';
import { readAttestationValue } from '../projectIntentAttestations';
import {
  coverageStatusFraction,
  type ReportPrimaryField,
  type ReportRelatedField,
  type ResolveReportFieldsResult,
} from './resolveReportFields';

function hasStoredKey(
  stored: Record<string, unknown> | undefined,
  key: string,
): boolean {
  if (!stored) return false;
  return Object.prototype.hasOwnProperty.call(stored, key)
    && stored[key] !== undefined;
}

/**
 * Remove schema-derived display fields from persisted preferences before any
 * resolution step. Legacy clients could save these keys; allowing that stale
 * value to participate in precedence would make a read-only row disagree with
 * its canonical project-intent source.
 */
function withoutReadOnlySetupFields(
  schema: readonly AuthoredFieldDef[],
  input: Record<string, unknown>,
): Record<string, unknown> {
  const canonical = { ...input };
  for (const fieldDef of schema) {
    if (fieldDef.readOnly === true) delete canonical[fieldDef.key];
  }
  return canonical;
}

function mergeEffectivePreferences<T extends ReportTypeId>(
  reportType: T,
  typedPreferences: TypedPreferencesByType | null | undefined,
  projectIntent: ProjectIntent | undefined | null,
  site?: SiteEntity | null,
  sed?: SiteEvidenceDocument | null,
): Record<string, unknown> {
  if (site) {
    return resolveEffectiveSetupPreferences(reportType, site, undefined, sed);
  }
  const inferred = (preferencesFromIntent(reportType, projectIntent ?? undefined, site ?? undefined)
    ?? {}) as Record<string, unknown>;
  const stored = withoutReadOnlySetupFields(
    getSetupSchemaForReportType(reportType),
    (typedPreferences?.[reportType] ?? {}) as Record<string, unknown>,
  );
  return { ...inferred, ...stored };
}

/** Schema fields for a report type's setup requirement (excludes notes). */
export function getSetupSchemaForReportType(reportType: ReportTypeId): readonly AuthoredFieldDef[] {
  const setupId = reportSetupRequirementId(reportType);
  const setup = getReportSetupChecklistFor(reportType).find(i => i.id === setupId);
  return setup?.authoredSchema?.filter(f => f.key !== 'notes') ?? [];
}

/**
 * Merge stored prefs, schema-level derivations, and intent inference.
 * Precedence: stored (authored) > derive > intent inference (provider).
 */
export function resolveEffectiveSetupPreferences<T extends ReportTypeId>(
  reportType: T,
  site: Pick<SiteEntity, 'projectIntent' | 'typedPreferencesDefaults' | 'id' | 'name' | 'siteArea' | 'address' | 'studyArea' | 'siteBoundaries' | 'siteBoundary' | 'siteLayers'>,
  schema?: readonly AuthoredFieldDef[],
  sed?: SiteEvidenceDocument | null,
): Record<string, unknown> {
  site = applySiteStudyArea(site);
  const fieldSchema = schema ?? getSetupSchemaForReportType(reportType);
  const stored = withoutReadOnlySetupFields(
    fieldSchema,
    (site.typedPreferencesDefaults?.[reportType] ?? {}) as Record<string, unknown>,
  );
  const inferred = (preferencesFromIntent(reportType, site.projectIntent, site as SiteEntity)
    ?? {}) as Record<string, unknown>;
  const ctx: AuthoredFieldDeriveContext = {
    site: site as SiteEntity,
    projectIntent: site.projectIntent,
    sed,
  };

  const result = { ...inferred };
  for (const fieldDef of fieldSchema) {
    if (hasStoredKey(stored, fieldDef.key)) continue;
    if (fieldDef.derive) {
      const derived = fieldDef.derive(ctx);
      if (isFieldFilled(derived)) {
        result[fieldDef.key] = derived;
      }
    }
  }
  return { ...result, ...stored };
}

function readSiteIntentValue(
  key: string,
  intent: ProjectIntent | undefined | null,
  sources: InputSources,
): { value: unknown; sourceLabel?: string; displayValue?: string } {
  if (!intent) return { value: undefined };

  switch (key) {
    case 'powerRequirement':
      // V134-BUCKET-REBUILD — show the bucket DERIVED FROM the exact MW when
      // one is set (so 1750 MW resolves to the fine 'over-1500mw' tier),
      // instead of echoing a possibly-stale stored `powerRequirement` (e.g. an
      // old bucket-only edit left at the legacy 'over-200mw'). The coarse
      // bucket ladder already carries the >200 MW fine tiers, and
      // `effectivePower(intent).bucket` returns `bucketFromMw(mw)` when MW is
      // present, else the stored bucket — so this is value-preserving for
      // bucket-only intents and self-correcting for MW-backed ones.
      return { value: effectivePower(intent).bucket, sourceLabel: 'Site Project Intent' };
    case 'powerRequirementMW': {
      const resolved = resolveField<number>('projectMW', sources);
      if (resolved.value != null) {
        return {
          value: resolved.value,
          sourceLabel: resolved.sourceLayer === 'projectIntent'
            ? 'Site Project Intent'
            : 'Inferred from site context',
        };
      }
      return { value: undefined };
    }
    case 'workloadProfile':
      return {
        value: intent.workloadProfile ?? intent.projectType,
        sourceLabel: 'Site Project Intent',
      };
    case 'coolingStrategy': {
      // P0-5 — keep the raw stored value (downstream readers may expect it) but
      // normalise the DISPLAY through the canonical CoolingType label instead of
      // echoing the raw 'air'/'liquid'/'hybrid' enum string.
      const raw = intent.coolingStrategy;
      return {
        value: raw,
        sourceLabel: 'Site Project Intent',
        displayValue: raw != null ? COOLING_TYPE_LABEL[normaliseCooling(raw)] : undefined,
      };
    }
    case 'bopHeatRejection': {
      // P0-7 — cold-END heat-rejection SINK (drives water withdrawal). Labelled
      // via HEAT_REJECTION_LABELS.
      const raw = intent.bopHeatRejection;
      return {
        value: raw,
        sourceLabel: 'Site Project Intent',
        displayValue: raw != null ? HEAT_REJECTION_LABELS[raw] : undefined,
      };
    }
    case 'coolingGeneration': {
      // P0-7 — cold-PRODUCTION MACHINE (drives PUE / waste-heat). Project via the
      // back-compat boundary projector so legacy intents that only carry
      // `bopHeatRejection` still surface a sensible value; labelled via
      // COOLING_GENERATION_LABELS.
      const projected = projectCoolingGeneration(intent);
      // Only surface a value when it is meaningfully resolved (explicit field or
      // derivable from the sink); 'unknown' from a bare intent reads as empty.
      const resolved = intent.coolingGeneration != null || projected !== 'unknown'
        ? projected
        : undefined;
      return {
        value: resolved,
        sourceLabel: intent.coolingGeneration != null
          ? 'Site Project Intent'
          : 'Derived from heat-rejection sink',
        displayValue: resolved != null ? COOLING_GENERATION_LABELS[resolved] : undefined,
      };
    }
    case 'nuclearInterest':
      return { value: intent.nuclearInterest, sourceLabel: 'Site Project Intent' };
    case 'priority':
      return { value: intent.priority, sourceLabel: 'Site Project Intent' };
    case 'powerBasis':
      return { value: intent.powerBasis, sourceLabel: 'Site Project Intent' };
    case 'pueValue':
      return { value: intent.pueValue, sourceLabel: 'Site Project Intent' };
    case 'powerFactor':
      return { value: intent.powerFactor, sourceLabel: 'Site Project Intent' };
    case 'annualDemandUtilizationRangePct.low':
      return {
        value: intent.annualDemandUtilizationRangePct?.low,
        sourceLabel: 'Site Project Intent',
      };
    case 'annualDemandUtilizationRangePct.high':
      return {
        value: intent.annualDemandUtilizationRangePct?.high,
        sourceLabel: 'Site Project Intent',
      };
    case 'datacenterTierTarget':
      return { value: intent.datacenterTierTarget, sourceLabel: 'Site Project Intent' };
    case 'redundancyClass':
      return { value: intent.redundancyClass, sourceLabel: 'Site Project Intent' };
    case 'carbonCommitment':
      return { value: intent.carbonCommitment, sourceLabel: 'Site Project Intent' };
    // P1 (field-architecture audit §3.6/§4) — SIX net-new BTM-feasibility intent
    // fields. Read + label each (nested objects via dotted keys). All optional;
    // an unset / 'none' / 'undecided' value reads as empty in the review.
    case 'interconnectionStatus.queueStatus': {
      const v = intent.interconnectionStatus?.queueStatus;
      return {
        value: v && v !== 'none' ? v : undefined,
        sourceLabel: 'Site Project Intent',
        displayValue: v != null ? INTERCONNECTION_STATUS_LABELS[v] : undefined,
      };
    }
    case 'interconnectionStatus.queueId':
      return { value: intent.interconnectionStatus?.queueId, sourceLabel: 'Site Project Intent' };
    case 'interconnectionStatus.studyPhase': {
      const v = intent.interconnectionStatus?.studyPhase;
      return {
        value: v,
        sourceLabel: 'Site Project Intent',
        displayValue: v != null ? INTERCONNECTION_STUDY_PHASE_LABELS[v] : undefined,
      };
    }
    case 'fuelAccess.supply': {
      const v = intent.fuelAccess?.supply;
      return {
        value: v && v !== 'none' ? v : undefined,
        sourceLabel: 'Site Project Intent',
        displayValue: v != null ? FUEL_ACCESS_LABELS[v] : undefined,
      };
    }
    case 'fuelAccess.distanceKm':
      return { value: intent.fuelAccess?.distanceKm, sourceLabel: 'Site Project Intent' };
    case 'waterSource.source': {
      const kinds = waterSourceKinds(intent.waterSource);
      return {
        value: kinds.length > 0 ? kinds.join(',') : undefined,
        sourceLabel: 'Site Project Intent',
        displayValue: kinds.length > 0
          ? formatWaterSources(intent.waterSource, WATER_SOURCE_LABELS)
          : undefined,
      };
    }
    case 'waterSource.rightsStatus': {
      const v = intent.waterSource?.rightsStatus;
      return {
        value: v && v !== 'none' ? v : undefined,
        sourceLabel: 'Site Project Intent',
        displayValue: v != null ? WATER_RIGHTS_STATUS_LABELS[v] : undefined,
      };
    }
    case 'offtake.structure': {
      const v = intent.offtake?.structure;
      return {
        value: v,
        sourceLabel: 'Site Project Intent',
        displayValue: v != null ? OFFTAKE_STRUCTURE_LABELS[v] : undefined,
      };
    }
    case 'offtake.tenorYears':
      return { value: intent.offtake?.tenorYears, sourceLabel: 'Site Project Intent' };
    case 'offtake.pricePerMwhUsd':
      return { value: intent.offtake?.pricePerMwhUsd, sourceLabel: 'Site Project Intent' };
    case 'capacityCreditIntent.target': {
      const v = intent.capacityCreditIntent?.target;
      return {
        value: v && v !== 'none' ? v : undefined,
        sourceLabel: 'Site Project Intent',
        displayValue: v != null ? CAPACITY_CREDIT_TARGET_LABELS[v] : undefined,
      };
    }
    case 'capacityCreditIntent.targetMW':
      return { value: intent.capacityCreditIntent?.targetMW, sourceLabel: 'Site Project Intent' };
    case 'inServiceDate': {
      const resolved = resolveField<string>('inServiceDate', sources);
      return {
        value: resolved.value ?? intent.inServiceDate,
        sourceLabel: 'Site Project Intent',
      };
    }
    default: {
      // R-131-EPISTEMIC-GROUNDING-E — attestations.* keys on Coverage Matrix
      if (key.startsWith('attestations.')) {
        const attKey = key.slice('attestations.'.length);
        const value = readAttestationValue(intent.attestations, attKey);
        return {
          value,
          sourceLabel: value ? 'Operator attestation' : 'Site Project Intent',
        };
      }
      return { value: undefined };
    }
  }
}

function readSetupPrefValue(
  key: string,
  reportType: ReportTypeId,
  effective: Record<string, unknown>,
  stored: Record<string, unknown>,
): { value: unknown; sourceLabel?: string; provenance: 'authored' | 'derived' | 'provider' | 'missing' } {
  if (!key) return { value: undefined, provenance: 'missing' };

  // KPI weight flatten keys
  if (key.startsWith('kpi') && key.endsWith('Weight')) {
    const dim = key.replace(/^kpi/, '').replace(/Weight$/, '').toLowerCase();
    const mapKey = dim as keyof NonNullable<FeasibilityPreferences['kpiWeightOverrides']>;
    const overrides = effective.kpiWeightOverrides as FeasibilityPreferences['kpiWeightOverrides'];
    const value = overrides?.[mapKey];
    const fromStored = (stored.kpiWeightOverrides as FeasibilityPreferences['kpiWeightOverrides'])?.[mapKey];
    if (value === undefined) return { value: undefined, provenance: 'missing' };
    return {
      value,
      sourceLabel: fromStored !== undefined ? 'Site report config' : 'Inferred from intent',
      provenance: fromStored !== undefined ? 'authored' : 'provider',
    };
  }

  // Interconnection latency flatten
  if (key === 'latencyAwsMs' || key === 'latencyAzureMs' || key === 'latencyGcpMs') {
    const cloud = key === 'latencyAwsMs' ? 'aws' : key === 'latencyAzureMs' ? 'azure' : 'gcp';
    const latency = effective.latencyTargetsMs as InterconnectionPreferences['latencyTargetsMs'];
    const value = latency?.[cloud];
    const storedLatency = stored.latencyTargetsMs as InterconnectionPreferences['latencyTargetsMs'];
    if (value === undefined) return { value: undefined, provenance: 'missing' };
    return {
      value,
      sourceLabel: storedLatency?.[cloud] !== undefined ? 'Site report config' : 'Inferred from intent',
      provenance: storedLatency?.[cloud] !== undefined ? 'authored' : 'provider',
    };
  }

  if (key === 'carrierDiversityMin') {
    const raw = effective.carrierDiversityMin;
    const value = raw === undefined ? undefined : String(raw);
    if (value === undefined) return { value: undefined, provenance: 'missing' };
    return {
      value,
      sourceLabel: hasStoredKey(stored, 'carrierDiversityMin') ? 'Site report config' : 'Inferred from intent',
      provenance: hasStoredKey(stored, 'carrierDiversityMin') ? 'authored' : 'provider',
    };
  }

  if (key === 'speciesConcerns') {
    const arr = effective.speciesConcerns as string[] | undefined;
    const value = arr?.length ? arr.join(', ') : undefined;
    if (value === undefined) return { value: undefined, provenance: 'missing' };
    return {
      value,
      sourceLabel: hasStoredKey(stored, 'speciesConcerns') ? 'Site report config' : 'Inferred from intent',
      provenance: hasStoredKey(stored, 'speciesConcerns') ? 'authored' : 'provider',
    };
  }

  if (key === 'questions') {
    const questions = effective.questions;
    const texts = Array.isArray(questions)
      ? questions
        .map((q) => (typeof q === 'object' && q && 'text' in q ? (q as { text?: unknown }).text : undefined))
        .filter((text): text is string => typeof text === 'string')
        .map((text) => text.trim())
        .filter(Boolean)
      : [];
    const value = texts.length > 0 ? texts.join('\n') : undefined;
    if (value === undefined) return { value: undefined, provenance: 'missing' };
    return {
      value,
      sourceLabel: hasStoredKey(stored, 'questions') ? 'Site report config' : 'Inferred from intent',
      provenance: hasStoredKey(stored, 'questions') ? 'authored' : 'provider',
    };
  }

  if (key === 'sourceReportIds') {
    const sourceReportIds = effective.sourceReportIds;
    const ids = Array.isArray(sourceReportIds)
      ? sourceReportIds.filter((id): id is string => typeof id === 'string')
      : [];
    const value = ids.length > 0 ? ids.join(', ') : undefined;
    if (value === undefined) return { value: undefined, provenance: 'missing' };
    return {
      value,
      sourceLabel: hasStoredKey(stored, 'sourceReportIds') ? 'Site report config' : 'Inferred from intent',
      provenance: hasStoredKey(stored, 'sourceReportIds') ? 'authored' : 'provider',
    };
  }

  const value = effective[key];
  if (value === undefined) return { value: undefined, provenance: 'missing' };
  return {
    value,
    sourceLabel: hasStoredKey(stored, key) ? 'Site report config' : 'Inferred from intent',
    provenance: hasStoredKey(stored, key) ? 'authored' : 'provider',
  };
}

export function resolveReportSetupFields(args: {
  requirement: ChecklistItem;
  reportType?: ReportTypeId;
  projectIntent?: ProjectIntent | null;
  siteContext?: SiteContext | null;
  typedPreferences?: TypedPreferencesByType | null;
  site?: SiteEntity | null;
  sed?: SiteEvidenceDocument | null;
}): ResolveReportFieldsResult {
  const {
    requirement,
    reportType: reportTypeArg,
    projectIntent,
    siteContext,
    typedPreferences,
    site,
  } = args;

  const schema = requirement.authoredSchema ?? [];
  const structuredFields = schema.filter(f => f.key !== 'notes');
  const sources: InputSources = {
    projectIntent: projectIntent ?? undefined,
    siteContext: siteContext ?? undefined,
    reportPrefs: typedPreferences ?? undefined,
  };

  const setupReportType =
    setupReportTypeForRequirement(requirement.id) ?? reportTypeArg;
  const storedPrefs = setupReportType
    ? withoutReadOnlySetupFields(
        structuredFields,
        (typedPreferences?.[setupReportType] ?? {}) as Record<string, unknown>,
      )
    : {};
  const setupSchema = setupReportType ? structuredFields : [];
  const deriveCtx: AuthoredFieldDeriveContext = {
    site: (site ?? { id: '', name: '', projectIntent: projectIntent ?? undefined, typedPreferencesDefaults: typedPreferences ?? undefined }) as SiteEntity,
    projectIntent: projectIntent ?? undefined,
    sed: args.sed,
  };
  const effectivePrefs = setupReportType && site
    ? resolveEffectiveSetupPreferences(setupReportType, site, setupSchema, args.sed)
    : setupReportType
      ? mergeEffectivePreferences(setupReportType, typedPreferences, projectIntent, site)
      : {};

  const primary: ReportPrimaryField[] = structuredFields.map(fieldDef => {
    let value: unknown;
    let sourceLabel: string | undefined;
    let provenance: ReportPrimaryField['provenance'] = 'missing';
    // Optional per-field display override (e.g. cooling axes routed through the
    // canonical label maps instead of echoing the raw enum string). Falls back
    // to formatFieldValue(value) when absent.
    let displayOverride: string | undefined;

    if (requirement.id === SITE_PROJECT_INTENT_ID) {
      const read = readSiteIntentValue(fieldDef.key, projectIntent, sources);
      value = read.value;
      sourceLabel = read.sourceLabel;
      displayOverride = read.displayValue;
      provenance = isFieldFilled(value) ? 'authored' : 'missing';
    } else if (setupReportType) {
      const read = readSetupPrefValue(fieldDef.key, setupReportType, effectivePrefs, storedPrefs);
      if (read.provenance === 'authored') {
        value = read.value;
        sourceLabel = read.sourceLabel;
        provenance = 'authored';
      } else if (fieldDef.derive) {
        const derived = fieldDef.derive(deriveCtx);
        if (isFieldFilled(derived)) {
          value = derived;
          sourceLabel = 'Derived from site data';
          provenance = 'derived';
        } else {
          value = read.value;
          sourceLabel = read.sourceLabel;
          provenance = isFieldFilled(value) ? 'provider' : 'missing';
        }
      } else {
        value = read.value;
        sourceLabel = read.sourceLabel;
        provenance = isFieldFilled(value) ? read.provenance : 'missing';
      }
    }

    const displayValue = isFieldFilled(value)
      ? (displayOverride ?? formatFieldValue(value))
      : '—';
    return {
      key: fieldDef.key,
      label: fieldDef.label,
      value,
      displayValue,
      provenance,
      sourceLabel,
      required: fieldDef.required === true,
      editable: fieldDef.readOnly !== true,
      isEmpty: !isFieldFilled(value),
    };
  });

  const related: ReportRelatedField[] = [];
  const structuredFilled = primary.filter(f => !f.isEmpty).length;
  const structuredTotal = primary.length;
  const { filled: statusFilled, total: statusTotal } = coverageStatusFraction({ primary, related });

  return {
    primary,
    related,
    hasSchema: structuredTotal > 0,
    structuredFilled,
    structuredTotal,
    statusFilled,
    statusTotal,
  };
}

/** Merge inferred + stored + derived typed prefs for wizard / matrix parity. */
export function resolveWizardPreferencesForType<T extends ReportTypeId>(
  reportType: T,
  site: Pick<SiteEntity, 'projectIntent' | 'typedPreferencesDefaults' | 'id' | 'name' | 'siteArea' | 'address' | 'studyArea' | 'siteBoundaries' | 'siteBoundary' | 'siteLayers'>,
  sed?: SiteEvidenceDocument | null,
): TypedPreferencesByType[T] | undefined {
  const effective = resolveEffectiveSetupPreferences(reportType, site, undefined, sed);
  if (Object.keys(effective).length === 0) return undefined;
  return effective as unknown as TypedPreferencesByType[T];
}

/** Convert inline setup form values back onto typed preferences. */
export function setupFormValuesToPreferences(
  reportType: ReportTypeId,
  formFields: Record<string, string | number | boolean>,
  existing?: TypedPreferencesByType[ReportTypeId],
): NonNullable<TypedPreferencesByType[ReportTypeId]> {
  const base = { ...(existing ?? {}) } as Record<string, unknown>;
  const readOnlyKeys = new Set(
    getSetupSchemaForReportType(reportType)
      .filter(fieldDef => fieldDef.readOnly === true)
      .map(fieldDef => fieldDef.key),
  );
  // Old clients could persist a schema-derived display field alongside real
  // preferences. Remove those stale keys from both the existing object and
  // the submitted form so saving any other field repairs the site default.
  for (const key of readOnlyKeys) delete base[key];
  const fields = Object.fromEntries(
    Object.entries(formFields).filter(([key]) => !readOnlyKeys.has(key)),
  ) as Record<string, string | number | boolean>;

  if (reportType === 'interconnection') {
    const next = { ...base, ...fields } as InterconnectionPreferences;
    if (fields.bandwidthGbps !== undefined && fields.bandwidthGbps !== '') {
      next.bandwidthGbps = Number(fields.bandwidthGbps);
    }
    if (fields.carrierDiversityMin !== undefined && fields.carrierDiversityMin !== '') {
      next.carrierDiversityMin = Number(fields.carrierDiversityMin) as 1 | 2 | 3;
    }
    const latency = { ...(next.latencyTargetsMs ?? {}) };
    if (fields.latencyAwsMs !== undefined && fields.latencyAwsMs !== '') { latency.aws = Number(fields.latencyAwsMs); delete (next as any).latencyAwsMs; }
    if (fields.latencyAzureMs !== undefined && fields.latencyAzureMs !== '') { latency.azure = Number(fields.latencyAzureMs); delete (next as any).latencyAzureMs; }
    if (fields.latencyGcpMs !== undefined && fields.latencyGcpMs !== '') { latency.gcp = Number(fields.latencyGcpMs); delete (next as any).latencyGcpMs; }
    if (Object.keys(latency).length > 0) next.latencyTargetsMs = latency;
    if (fields.ixPeeringRequired !== undefined) next.ixPeeringRequired = Boolean(fields.ixPeeringRequired);
    if (fields.submarineCableProximityImportant !== undefined) {
      next.submarineCableProximityImportant = Boolean(fields.submarineCableProximityImportant);
    }
    return next as NonNullable<TypedPreferencesByType[ReportTypeId]>;
  }

  if (reportType === 'feasibility' || reportType === 'supply-chain-risk') {
    const next = { ...base, ...fields } as FeasibilityPreferences;
    const weights = { ...(next.kpiWeightOverrides ?? {}) };
    const map: Record<string, keyof NonNullable<FeasibilityPreferences['kpiWeightOverrides']>> = {
      kpiGridWeight: 'grid',
      kpiCoolingWeight: 'cooling',
      kpiSiteWeight: 'site',
      kpiRegulatoryWeight: 'regulatory',
      kpiLogisticsWeight: 'logistics',
    };
    for (const [formKey, dim] of Object.entries(map)) {
      const raw = fields[formKey];
      if (raw !== undefined && raw !== '') {
        weights[dim] = Number(raw);
      }
      delete (next as any)[formKey];
    }
    if (Object.keys(weights).length > 0) next.kpiWeightOverrides = weights;
    return next as NonNullable<TypedPreferencesByType[ReportTypeId]>;
  }

  if (reportType === 'custom-brief') {
    const next = { ...(base as unknown as CustomBriefPreferences) };
    if (fields.briefName !== undefined) next.briefName = String(fields.briefName);
    if (fields.objective !== undefined) next.objective = String(fields.objective);
    if (fields.contextNotes !== undefined) next.contextNotes = String(fields.contextNotes);
    if (fields.counterparties !== undefined) next.counterparties = String(fields.counterparties);
    if (fields.keyDates !== undefined) next.keyDates = String(fields.keyDates);
    if (fields.questions !== undefined) {
      const lines = String(fields.questions).split('\n').map(s => s.trim()).filter(Boolean);
      next.questions = lines.map((text, i) => ({
        id: `q${i}`,
        text,
      }));
    }
    return next as NonNullable<TypedPreferencesByType[ReportTypeId]>;
  }

  if (reportType === 'executive-summary') {
    const next = { ...(base as unknown as ExecutiveSummaryPreferences) };
    if (fields.sourceReportIds !== undefined) {
      next.sourceReportIds = String(fields.sourceReportIds)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
    }
    if (fields.synthesisFocus !== undefined) next.synthesisFocus = String(fields.synthesisFocus);
    return next as NonNullable<TypedPreferencesByType[ReportTypeId]>;
  }

  if (reportType === 'environmental' && fields.speciesConcerns !== undefined) {
    const next = { ...base };
    const raw = String(fields.speciesConcerns);
    next.speciesConcerns = raw.split(',').map(s => s.trim()).filter(Boolean);
    for (const [k, v] of Object.entries(fields)) {
      if (k === 'speciesConcerns') continue;
      next[k] = v;
    }
    return next as NonNullable<TypedPreferencesByType[ReportTypeId]>;
  }

  return { ...base, ...fields } as NonNullable<TypedPreferencesByType[ReportTypeId]>;
}

export function evaluateSetupRequirementCompleteness(
  requirement: ChecklistItem,
  resolved: ResolveReportFieldsResult,
  reportType?: ReportTypeId,
  typedPreferences?: TypedPreferencesByType | null,
  projectIntent?: ProjectIntent | null,
  site?: SiteEntity | null,
): {
  meetsStrongBar: boolean;
  meetsAdequateBar: boolean;
  meetsTentativeBar: boolean;
  validatorValid: boolean;
} {
  const required = resolved.primary.filter(f => f.required);
  const requiredFilled = required.filter(f => !f.isEmpty).length;
  const requiredAllFilled = required.length === 0 || requiredFilled === required.length;
  const structuredFraction =
    resolved.structuredTotal > 0
      ? resolved.structuredFilled / resolved.structuredTotal
      : 0;

  let validatorValid = true;
  if (requirement.kind === 'report-setup') {
    const setupType = setupReportTypeForRequirement(requirement.id) ?? reportType;
    if (setupType) {
      const effective = site
        ? resolveEffectiveSetupPreferences(setupType, site)
        : mergeEffectivePreferences(setupType, typedPreferences, projectIntent);
      validatorValid = validatePreferencesFor(setupType, effective).valid;
    }
  } else if (requirement.kind === 'site-intent') {
    validatorValid = requiredAllFilled && resolved.structuredFilled > 0;
  }

  if (resolved.structuredTotal === 0) {
    return {
      meetsStrongBar: false,
      meetsAdequateBar: false,
      meetsTentativeBar: false,
      validatorValid,
    };
  }

  if (requirement.kind === 'report-setup') {
    // `validatorValid` alone is not evidence, and neither is a non-empty
    // field count. `validatePreferencesFor` runs against preferences merged
    // over DEFAULTS, so it passes on a setup the user has never opened; and
    // `structuredFilled` counts system-supplied values, so a row can report
    // a filled field it invented itself. On `feasibility-report-setup` with
    // no user input at all, `btmStrategy` resolves to `'grid-only'` with
    // `provenance: 'derived'` while the other five fields are `'missing'` —
    // enough, before this, to badge the row `adequate` and count it as
    // covered on the Evidence readiness gauge. The tell was the same
    // non-monotonic result as the vacuous required-field bar in
    // `evaluateRequirementCompleteness`: `meetsAdequateBar: true` alongside
    // a `meetsTentativeBar` the row could fail.
    //
    // What the bar needs is a USER-SUPPLIED BASIS. Field provenance alone
    // cannot supply it: a `derive()` grounded in the user's project intent
    // and a `derive()` returning a constant fallback both tag `'derived'`.
    // `btmStrategy` is exactly that — it yields `'grid-only'` whether or not
    // any intent exists. So the basis is tested at the SOURCE instead:
    //
    //   - stored typed prefs for this report type  → `provenance: 'authored'`
    //   - a project intent the derive fns run over → `projectIntent`
    //
    // Either one means a human supplied the inputs this setup rests on. J8
    // depends on the second: `computeReadinessGaps` must not report a
    // report's own setup as a false gap on a site whose intent populates it,
    // and intent-derived values never carry `'authored'`.
    //
    // Weak values still register as `tentative` — something is present, the
    // user just has not supplied any of it. And this does not nag on
    // finished reports: `computeReadinessGaps` drops a report's OWN setup
    // row once `hasGeneratedReport` is true, since a report that generated
    // had, by definition, a valid setup.
    const hasStoredPrefInput = resolved.primary.some(
      f => f.provenance === 'authored' && !f.isEmpty,
    );
    const hasUserSuppliedBasis =
      hasStoredPrefInput || projectIntent != null || site?.projectIntent != null;
    return {
      meetsStrongBar:
        validatorValid && hasUserSuppliedBasis && requiredAllFilled && structuredFraction >= 0.8,
      meetsAdequateBar: validatorValid && hasUserSuppliedBasis,
      meetsTentativeBar: resolved.structuredFilled > 0,
      validatorValid,
    };
  }

  const meetsStrongBar =
    validatorValid
    && requiredAllFilled
    && structuredFraction >= 0.8;
  const meetsAdequateBar =
    validatorValid
    && (requiredAllFilled || structuredFraction >= 0.5);
  const meetsTentativeBar = resolved.structuredFilled > 0;

  return {
    meetsStrongBar,
    meetsAdequateBar,
    meetsTentativeBar,
    validatorValid,
  };
}
